import React, { useState } from 'react';
import { setDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
    const { user, login, registerWithPassword, loginWithPassword, resetPassword, userDoc, userDocLoading } = useAuth();
    const [applied, setApplied] = useState(false);
    const [applying, setApplying] = useState(false);
    const [applyMsg, setApplyMsg] = useState('');
    const [email, setEmail] = useState('');
    const [loginMethod, setLoginMethod] = useState('choose');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isRegistering, setIsRegistering] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [registering, setRegistering] = useState(false);
    const [loggingIn, setLoggingIn] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    const [resettingPassword, setResettingPassword] = useState(false);

    const handleApply = async () => {
        if (!user) return;
        setApplying(true);
        try {
            const appRef = doc(db, 'applications', user.email);
            const existing = await getDoc(appRef);
            if (!existing.exists()) {
                await setDoc(appRef, {
                    email: user.email,
                    name: user.displayName || '',
                    appliedAt: new Date(),
                });
                setApplyMsg('Application submitted! Please wait for admin approval.');
            } else {
                setApplyMsg('You have already applied. Please wait for admin approval.');
            }
            setApplied(true);
        } catch (e) {
            setApplyMsg('Failed to apply. Please try again later.');
        }
        setApplying(false);
    };

    const handlePasswordRegister = async () => {
        if (!email || !email.includes('@')) {
            setPasswordError('Please enter a valid email address');
            return;
        }
        if (!password || password.length < 6) {
            setPasswordError('Password must be at least 6 characters long');
            return;
        }
        if (password !== confirmPassword) {
            setPasswordError('Passwords do not match');
            return;
        }

        setRegistering(true);
        setPasswordError('');
        const result = await registerWithPassword(email, password);
        if (!result.success) {
            setPasswordError(result.error || 'Failed to register');
        }
        setRegistering(false);
    };

    const handlePasswordLogin = async () => {
        if (!email || !email.includes('@')) {
            setPasswordError('Please enter a valid email address');
            return;
        }
        if (!password) {
            setPasswordError('Please enter your password');
            return;
        }

        setLoggingIn(true);
        setPasswordError('');
        const result = await loginWithPassword(email, password);
        if (!result.success) {
            setPasswordError(result.error || 'Failed to sign in');
        }
        setLoggingIn(false);
    };

    const handleForgotPassword = async () => {
        if (!email || !email.includes('@')) {
            setPasswordError('Please enter a valid email address');
            return;
        }

        setResettingPassword(true);
        setPasswordError('');
        const result = await resetPassword(email);
        if (result.success) {
            setResetEmailSent(true);
        } else {
            setPasswordError(result.error || 'Failed to send reset email');
        }
        setResettingPassword(false);
    };

    const resetPasswordForm = () => {
        setPassword('');
        setConfirmPassword('');
        setPasswordError('');
        setEmail('');
        setShowForgotPassword(false);
        setResetEmailSent(false);
    };

    if (userDocLoading) return <div></div>;

    return (
        <div className="login-page">
            <div className="card login-card">
                {loginMethod === 'password' && (
                    <button
                        className="btn login-back-btn"
                        onClick={() => { setLoginMethod('choose'); resetPasswordForm(); }}
                    >
                        <img src="/angles-left-svgrepo-com.svg" alt="Back" style={{ width: '20px', height: '20px', filter: 'brightness(0) saturate(100%) invert(20%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0.2) contrast(1)' }} />
                    </button>
                )}

                <div style={{ marginBottom: 24 }}>
                    <span style={{ fontSize: '2.7rem', color: 'var(--color-primary)', display: 'inline-block', marginBottom: 8 }}>📊</span>
                    <h2 style={{ fontWeight: 700, color: '#333', marginBottom: 6 }}>Sign in to GLACC</h2>
                    <div style={{ fontSize: '1.08rem', color: '#666', marginBottom: 18 }}>Access the classifier and admin tools</div>
                </div>

                {user && (!userDoc || !userDoc.allow) ? (
                    <div className="alert alert-error" style={{ margin: '1.5rem 0', fontSize: '1.05rem' }}>
                        <div style={{ marginBottom: 8 }}>
                            <b>{user.email}</b> does not have access.<br />
                            Please contact the website admin to get permission.
                        </div>
                        {!applied && (
                            <button className="btn btn-primary" style={{ marginTop: '1.2rem', width: '100%' }} onClick={handleApply} disabled={applying}>
                                {applying ? 'Applying...' : 'Apply for Access'}
                            </button>
                        )}
                        {applyMsg && <div className="alert alert-success" style={{ marginTop: 12, fontSize: '0.98rem' }}>{applyMsg}</div>}
                    </div>
                ) : loginMethod === 'choose' ? (
                    <div className="login-choices">
                        <button className="btn btn-primary login-btn" onClick={login}>
                            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" style={{ width: 24, height: 24 }} />
                            Sign in with Google
                        </button>
                        <div className="login-divider">
                            <div className="login-divider__line"></div>
                            <span className="login-divider__text">or</span>
                            <div className="login-divider__line"></div>
                        </div>
                        <button className="btn btn-teal login-btn" onClick={() => setLoginMethod('password')}>
                            Sign in with Password
                        </button>
                    </div>
                ) : loginMethod === 'password' ? (
                    <div>
                        <div style={{ marginBottom: '1.5rem' }}>
                            {resetEmailSent ? (
                                <div className="alert alert-success" style={{ margin: '1.5rem 0', fontSize: '1.05rem' }}>
                                    <div style={{ marginBottom: 8 }}>
                                        <b>Check your email!</b><br />
                                        We've sent a password reset link to <b>{email}</b><br />
                                        Click the link in your email to reset your password.
                                    </div>
                                    <button className="btn btn-teal" style={{ marginTop: '1rem', width: '100%' }} onClick={() => { setResetEmailSent(false); setShowForgotPassword(false); }}>
                                        Back to Sign In
                                    </button>
                                </div>
                            ) : showForgotPassword ? (
                                <div>
                                    <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                                        <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>Reset Password</h3>
                                        <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>Enter your email address and we'll send you a link to reset your password.</p>
                                    </div>
                                    <input type="email" className="form-control login-input" placeholder="Enter your email address" value={email} onChange={(e) => setEmail(e.target.value)} />
                                    {passwordError && <div className="alert alert-error" style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>{passwordError}</div>}
                                    <button className="btn btn-teal" style={{ width: '100%', marginBottom: '1rem' }} onClick={handleForgotPassword} disabled={resettingPassword}>
                                        {resettingPassword ? 'Sending...' : 'Send Reset Link'}
                                    </button>
                                    <button className="btn btn-gray" style={{ width: '100%', marginBottom: '1rem' }} onClick={() => { setShowForgotPassword(false); setPasswordError(''); }}>
                                        Back to Sign In
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <input type="email" className="form-control login-input" placeholder="Enter your email address" value={email} onChange={(e) => setEmail(e.target.value)} />
                                    <input type="password" className="form-control login-input" placeholder={isRegistering ? "Create a password (min 6 characters)" : "Enter your password"} value={password} onChange={(e) => setPassword(e.target.value)} />
                                    {isRegistering && (
                                        <input type="password" className="form-control login-input" placeholder="Confirm your password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                                    )}
                                    {passwordError && <div className="alert alert-error" style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>{passwordError}</div>}

                                    <button className="btn btn-teal" style={{ width: '100%', marginBottom: '1rem' }} onClick={isRegistering ? handlePasswordRegister : handlePasswordLogin} disabled={registering || loggingIn}>
                                        {registering ? 'Creating Account...' : loggingIn ? 'Signing In...' : (isRegistering ? 'Create Account' : 'Sign In')}
                                    </button>

                                    {!isRegistering && (
                                        <button className="btn login-forgot-btn" onClick={() => { setShowForgotPassword(true); setPasswordError(''); }}>
                                            Forgot your password?
                                        </button>
                                    )}

                                    <button className="btn btn-gray" style={{ width: '100%', marginBottom: '1rem' }} onClick={() => { setIsRegistering(!isRegistering); resetPasswordForm(); }}>
                                        {isRegistering ? 'Already have an account? Sign In' : 'Need an account? Register'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
